import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSettings } from '../contexts/SettingsContext';

const AI_AUTOMATION_URL = process.env.REACT_APP_AI_AUTOMATION_URL || 'http://localhost:8100/api/v1';

/* ── SVG Icons ── */
const CheckIcon = () => (
  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);
const ErrorIcon = () => (
  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const SpinnerIcon = () => (
  <svg className="w-4 h-4 text-blue-500 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);
const QueueIcon = () => (
  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const DuplicateIcon = () => (
  <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
  </svg>
);

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'completed': return <CheckIcon />;
    case 'duplicate': return <DuplicateIcon />;
    case 'failed': return <ErrorIcon />;
    case 'processing': return <SpinnerIcon />;
    default: return <QueueIcon />;
  }
};

const statusColor = (status) => {
  switch (status) {
    case 'completed': return 'text-green-600 dark:text-green-400';
    case 'duplicate': return 'text-blue-500 dark:text-blue-400';
    case 'failed': return 'text-red-500 dark:text-red-400';
    case 'processing': return 'text-blue-500 dark:text-blue-400';
    default: return 'text-gray-500 dark:text-gray-400';
  }
};

const truncateFilename = (name, maxLen = 40) => {
  if (!name || name.length <= maxLen) return name;
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
  const base = name.slice(0, name.lastIndexOf('.') || name.length);
  return `${base.slice(0, maxLen - ext.length - 3)}...${ext}`;
};

const isTruthyEnv = (value) => ['1', 'true', 'yes', 'on'].includes(String(value || '').trim().toLowerCase());

const hexToRgba = (hex, alpha) => {
  const fallback = `rgba(59, 91, 219, ${alpha})`;
  if (!hex || typeof hex !== 'string') return fallback;
  const normalized = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return fallback;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatDuration = (ms) => {
  const value = Number(ms);
  if (!Number.isFinite(value) || value < 0) return null;
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 1 : 2)}s`;
  return `${Math.round(value)}ms`;
};

const getModelLabel = (debug) => [debug?.provider, debug?.model].filter(Boolean).join(' / ');
const getDurationLabel = (log) => formatDuration(log?.debug?.processing_time_ms ?? log?.processing_time_ms);
const getStatusTimeLabel = (log) => {
  if (!log) return null;
  if (log.status === 'processing') return 'Processing...';
  const duration = getDurationLabel(log);
  return duration;
};

const DebugModal = ({ title, children, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-gray-900/45 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="resume-debug-modal-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[88vh] overflow-hidden rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700 px-5 py-4">
          <h3 id="resume-debug-modal-title" className="text-base font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            aria-label="Close debug details"
          >
            X
          </button>
        </div>
        <div className="max-h-[calc(88vh-70px)] overflow-y-auto px-5 py-4">
          {children}
        </div>
      </div>
    </div>
  );
};

const DebugField = ({ label, value, primaryColor }) => (
  <div>
    <div className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: primaryColor }}>
      {label}
    </div>
    <div className="text-sm text-gray-800 dark:text-gray-200 break-words">
      {value || 'Not captured'}
    </div>
  </div>
);

const DebugPre = ({ label, value, primaryColor }) => (
  <div>
    <div className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: primaryColor }}>
      {label}
    </div>
    <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-gray-50 dark:bg-gray-950/70 border border-gray-200 dark:border-gray-700 p-3 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
      {value || 'Not captured'}
    </pre>
  </div>
);

const BatchDevDetailsModal = ({ summary, primaryColor, onClose }) => (
  <DebugModal title="Resume Parsing Debug Details" onClose={onClose}>
    <div className="space-y-4">
      <DebugField label="Model Used" value={summary.modelLabel} primaryColor={primaryColor} />
      <DebugField label="Total Processing Time" value={summary.totalProcessingLabel} primaryColor={primaryColor} />
      <DebugPre label="Prompt Used" value={summary.prompt} primaryColor={primaryColor} />
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: primaryColor }}>
          Processing Time Summary
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {summary.timeSummaries.map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950/50 px-3 py-2"
            >
              <div className="text-[11px] text-gray-500 dark:text-gray-400">{item.label}</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </DebugModal>
);

const DevDetailsHeaderButton = ({ onClick, primaryColor }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-xs font-semibold rounded-md border px-2 py-1 transition-colors bg-white/80 dark:bg-gray-900/60 hover:bg-gray-50 dark:hover:bg-gray-800"
    style={{ borderColor: hexToRgba(primaryColor, 0.28), color: primaryColor }}
  >
    View Dev Details
  </button>
);

const HeaderActionButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-xs text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors px-2 py-1"
  >
    {children}
  </button>
);

const InlineDebugSummary = ({ log }) => {
  const label = getStatusTimeLabel(log);
  if (!label) return null;
  return (
    <span className="text-xs text-gray-500 dark:text-gray-400">
      {label}
    </span>
  );
};

const getBatchSummary = (displayLogs) => {
  const debugEntries = displayLogs
    .map(log => ({ log, debug: log?.debug }))
    .filter(item => item.debug);
  const firstDebug = debugEntries.find(item => getModelLabel(item.debug) || item.debug.prompt)?.debug || {};
  const durations = debugEntries
    .map(item => Number(item.debug?.processing_time_ms))
    .filter(value => Number.isFinite(value) && value >= 0);
  const totalMs = durations.reduce((sum, value) => sum + value, 0);
  const averageMs = durations.length ? totalMs / durations.length : null;

  return {
    modelLabel: getModelLabel(firstDebug),
    prompt: firstDebug.prompt || '',
    totalProcessingLabel: totalMs > 0 ? `${formatDuration(totalMs)} across ${durations.length} file${durations.length === 1 ? '' : 's'}` : 'Processing...',
    timeSummaries: [
      { label: 'Files Uploaded', value: displayLogs.length },
      { label: 'Completed', value: displayLogs.filter(log => ['completed', 'duplicate'].includes(log.status)).length },
      { label: 'Failed', value: displayLogs.filter(log => log.status === 'failed').length },
      { label: 'Timed Files', value: durations.length },
      { label: 'Average Time', value: averageMs !== null ? formatDuration(averageMs) : 'Not captured' },
      { label: 'Processing', value: displayLogs.filter(log => ['processing', 'queued', 'queue', 'queued_retry'].includes(log.status)).length },
    ],
  };
};

/**
 * ResumeUploadLog — File-level parsing status log.
 * Persisted server-side in resume_parsing_log table (no localStorage).
 * Loads from server on mount, updates live via SSE fileLogs prop.
 */
const ResumeUploadLog = ({ userId, cid, fileLogs = [], isProcessing }) => {
  const { settings } = useSettings();
  const primaryColor = settings?.theme?.colors?.primary || '#3B5BDB';
  const [expanded, setExpanded] = useState(true);
  const [cleared, setCleared] = useState(false);
  const [savedLogs, setSavedLogs] = useState([]);
  const [developmentMode, setDevelopmentMode] = useState(isTruthyEnv(process.env.REACT_APP_DEVELOPMENT));
  const [showBatchDevDetails, setShowBatchDevDetails] = useState(false);
  const wasProcessingRef = useRef(false);
  const logEndRef = useRef(null);

  // Load from server on mount (survives page refresh)
  useEffect(() => {
    if (!userId || !cid) return;
    const fetchLogs = async () => {
      try {
        const url = `${AI_AUTOMATION_URL}/resumeparse/upload-logs/${encodeURIComponent(userId)}/${encodeURIComponent(cid)}`;
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          if (typeof data.development_mode === 'boolean') {
            setDevelopmentMode(data.development_mode);
          }
          if (data.file_logs && data.file_logs.length > 0) {
            setSavedLogs(data.file_logs);
            setCleared(false);
          }
        }
      } catch (e) {
        console.warn('[ResumeUploadLog] Failed to fetch saved logs:', e);
      }
    };
    fetchLogs();
  }, [userId, cid]);

  // Update saved logs when SSE provides new data
  useEffect(() => {
    if (fileLogs.length > 0) {
      setSavedLogs(fileLogs);
    }
  }, [fileLogs]);

  // When new processing starts, clear old logs
  useEffect(() => {
    if (isProcessing && !wasProcessingRef.current) {
      setCleared(false);
    }
    wasProcessingRef.current = isProcessing;
  }, [isProcessing]);

  // Auto-scroll
  useEffect(() => {
    if (expanded && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [fileLogs, expanded]);

  const handleClear = useCallback(async () => {
    setCleared(true);
    setSavedLogs([]);
    // Clear from server
    if (userId && cid) {
      try {
        await fetch(
          `${AI_AUTOMATION_URL}/resumeparse/upload-logs/${encodeURIComponent(userId)}/${encodeURIComponent(cid)}`,
          { method: 'DELETE' },
        );
      } catch { /* ignore */ }
    }
  }, [userId, cid]);

  // Use live SSE logs if available, otherwise show saved logs from server
  const displayLogs = fileLogs.length > 0 ? fileLogs : savedLogs;
  const batchDebugSummary = useMemo(() => getBatchSummary(displayLogs), [displayLogs]);

  if (cleared || (!displayLogs.length && !isProcessing)) return null;

  return (
    <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900 transition-colors">
      {/* Header */}
      <div className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 transition-colors">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="min-w-0 flex flex-1 items-center gap-2 flex-wrap text-left rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <svg className={`w-4 h-4 text-gray-500 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Upload Log</span>
          {developmentMode && (
            <span
              className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ backgroundColor: hexToRgba(primaryColor, 0.12), color: primaryColor }}
            >
              Development
            </span>
          )}
          {isProcessing ? (
            <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: primaryColor }}>Processing</span>
          ) : displayLogs.length > 0 && (() => {
            const completed = displayLogs.filter(l => l.status === 'completed' || l.status === 'duplicate').length;
            const failed = displayLogs.filter(l => l.status === 'failed').length;
            return (
              <>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  Uploaded {displayLogs.length}
                </span>
                {completed > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                    Completed {completed}
                  </span>
                )}
                {failed > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                    Failed {failed}
                  </span>
                )}
              </>
            );
          })()}
          {isProcessing && (
            <span className="text-xs text-gray-400 dark:text-gray-500">{displayLogs.length} file{displayLogs.length !== 1 ? 's' : ''}</span>
          )}
        </button>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {developmentMode && displayLogs.length > 0 && (
            <DevDetailsHeaderButton
              primaryColor={primaryColor}
              onClick={() => setShowBatchDevDetails(true)}
            />
          )}
          {!isProcessing && displayLogs.length > 0 && (
            <HeaderActionButton onClick={handleClear}>
              Clear
            </HeaderActionButton>
          )}
        </div>
      </div>

      {/* File list */}
      {expanded && (
        <div className="max-h-72 overflow-y-auto px-4 py-2 space-y-0.5">
          {displayLogs.length === 0 && isProcessing && (
            <div className="flex items-center gap-2 py-2 text-sm text-gray-500 dark:text-gray-400">
              <SpinnerIcon />
              <span>Waiting for parsing to start...</span>
            </div>
          )}
          {displayLogs.map((log, idx) => (
            <div
              key={`${log.file}-${idx}`}
              className={`flex items-start gap-2 py-1.5 ${log.status === 'failed' ? 'bg-red-50/50 dark:bg-red-900/5 -mx-2 px-2 rounded' : ''}`}
            >
              <div className="mt-0.5"><StatusIcon status={log.status} /></div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{truncateFilename(log.file)}</span>
                  {log.message && (
                    <span className={`text-xs ${statusColor(log.status)}`}>{log.message}</span>
                  )}
                  {developmentMode && <InlineDebugSummary log={log} />}
                </div>
              </div>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      )}
      {developmentMode && showBatchDevDetails && (
        <BatchDevDetailsModal
          summary={batchDebugSummary}
          primaryColor={primaryColor}
          onClose={() => setShowBatchDevDetails(false)}
        />
      )}
    </div>
  );
};

export default ResumeUploadLog;
